import ReactTooltip from "react-tooltip";
import { DonutChart } from "../../shared-components/DonutChart";
import { InsightCircle } from "../../shared-components/InsightCircle";

const INSIGHT_KEY_COLORS: Record<string, string> = {
  summary: "#2b3b72",
  fama_report: "#6cc695",
  news: "#f472b6",
  lexis_nexis_news: "#f472b6",
  legal_case: "#00aeef",
  lexis_nexis_cases: "#00aeef",
  legal: "#00aeef",
  community: "#2b3b72",
  community_count: "#2b3b72",
  adverse: "#6cc695",
  adverse_flag_count: "#6cc695",
  articles: "#f472b6",
  articles_count: "#f472b6",
  keywords: "#F48503",
};

const FALLBACK_SLICE_COLORS = [
  "#116798",
  "#F48503",
  "#8b5cf6",
  "#14b8a6",
] as const;

function stableFallbackColorIndex(key: string): number {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return Math.abs(h) % FALLBACK_SLICE_COLORS.length;
}

function colorForLegacyInsightKey(key: string): string {
  const mapped = INSIGHT_KEY_COLORS[key];
  if (mapped) return mapped;
  return FALLBACK_SLICE_COLORS[stableFallbackColorIndex(key)];
}

function coerceCount(v: unknown): number | null {
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string" && v !== "") {
    const parsed = Number(v);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}

/** Title case with spaces (matches completed-tab insight labels like "Legal Case"). */
function formatLegacyInsightKeyLabel(key: string): string {
  return key
    .replace(/_/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(
      (word) =>
        word.charAt(0).toLocaleUpperCase() + word.slice(1).toLocaleLowerCase()
    )
    .join(" ");
}

/** One row per own key in `insights` except `total`; values are numeric counts only. */
function legacyInsightsToEntries(
  insights: Record<string, unknown> | null | undefined
): { key: string; value: number }[] {
  if (!insights || typeof insights !== "object") return [];
  const out: { key: string; value: number }[] = [];
  for (const key of Object.keys(insights)) {
    if (key === "total") continue;
    const num = coerceCount(insights[key]);
    if (num === null) continue;
    out.push({ key, value: Math.max(0, num) });
  }
  return out.sort((a, b) => a.key.localeCompare(b.key));
}

function centerTotal(
  insights: Record<string, unknown> | null | undefined,
  entries: { key: string; value: number }[]
): number {
  if (!insights || typeof insights !== "object") return 0;
  const fromTotal = coerceCount(insights.total);
  if (fromTotal !== null) return Math.max(0, fromTotal);
  return entries.reduce((sum, e) => sum + e.value, 0);
}

export default function Legacy360InsightsCircle({
  insights,
  rowKey,
}: {
  insights: Record<string, unknown> | null | undefined;
  rowKey: string;
}) {
  const entries = legacyInsightsToEntries(insights);
  const total = centerTotal(insights, entries);
  const positive = entries.filter((e) => e.value > 0);
  const sliceSum = positive.reduce((sum, e) => sum + e.value, 0);
  /** When `insights.total` is 0 but breakdown keys are positive, center + legend must match slices. */
  const displayTotal = total > 0 ? total : sliceSum;
  const isTrulyEmpty = total === 0 && positive.length === 0;
  const tooltipId = `legacy360-insights-${rowKey}`;

  let chartData: { label: string; value: number; color: string }[] = [
    { label: "Default", value: 1, color: "#CAD0D3" },
  ];
  if (positive.length > 0) {
    chartData = positive.map((e) => ({
      label: formatLegacyInsightKeyLabel(e.key),
      value: e.value,
      color: colorForLegacyInsightKey(e.key),
    }));
  }

  return (
    <>
      <div className="flex justify-center items-center flex-none min-w-0">
        <InsightCircle
          flagkey={rowKey}
          tooltip={tooltipId}
          data={chartData}
          totalFlags={displayTotal}
        />
      </div>
      <ReactTooltip
        className="drop-shadow-2xl"
        backgroundColor="#FFFFFF"
        id={tooltipId}
      >
        <div className="flex flex-nowrap items-center gap-4 text-black w-max max-w-[calc(100vw-2rem)]">
          <div className="shrink-0 w-36">
            <DonutChart data={chartData} cutout={55} enableTooltip={false}>
              <span className="absolute top-[30%] left-[21%] text-center">
                <p className="text-xl">{displayTotal}</p>
                Total
                <br />
                Items
              </span>
            </DonutChart>
          </div>
          {isTrulyEmpty && (
            <div className="flex flex-row items-center px-10">
              <span>No Items Found</span>
            </div>
          )}
          {total > 0 && positive.length === 0 && (
            <div className="flex flex-row items-center px-10 shrink-0">
              <div className="whitespace-nowrap">
                <span>{formatLegacyInsightKeyLabel("total")}: </span>
                <span className="font-bold">{total}</span>
                <span className="ml-1 text-gray-600">(no breakdown keys)</span>
              </div>
            </div>
          )}
          {positive.length > 0 && (
            <div className="border-l border-gray-300 pl-4 ml-4 flex flex-col justify-center gap-2 shrink-0">
              {positive.map((e) => {
                const label = formatLegacyInsightKeyLabel(e.key);
                return (
                  <div
                    key={e.key}
                    className="flex flex-row items-center gap-2 whitespace-nowrap"
                  >
                    <div
                      className="w-2 h-2 rounded-full aspect-ratio-1/1 scale-1.2 shrink-0"
                      style={{
                        backgroundColor: colorForLegacyInsightKey(e.key),
                      }}
                      aria-hidden
                    />
                    <div className="whitespace-nowrap">
                      <span>{label}: </span>
                      <span className="font-bold">{e.value}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </ReactTooltip>
    </>
  );
}
